import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import {
  RequestState,
  useAsyncOperationState,
} from '../../../../../dorian-shared/hooks/useAsyncOperationState';
import { Promotion } from '../../types/PromotionTypes';
import {
  PromotionContestActiveSchema,
  PromotionContestFormValues,
} from './types/ContestTypes';
import { api } from '../../../../api';
import { showToast } from '../../../../ui/utils';
import { missingStoryUuidMessage } from './components/ContestSection';
import { ContestForm } from './ContestForm';
import {
  buildContestSavePayload,
  contestPayloadToFormValues,
  createEmptyContestFormValues,
  PromotionContestResponse,
} from './contestUtils';

interface ContestEditorProps {
  promotion: Promotion;
  isEditor: boolean;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function LoadingState(props: { requestState: RequestState }) {
  switch (props.requestState) {
    case RequestState.Loading:
      return (
        <div className="text-center py-3 mt-3">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    case RequestState.Error:
      return (
        <Alert variant="danger" className="mb-0 mt-3">
          Failed to load Contests configuration. Please try again later.
        </Alert>
      );
    default:
      return null;
  }
}

export function ContestEditor(props: ContestEditorProps) {
  const { promotion, isEditor, onSaved, onDirtyChange } = props;
  const promotionHasSchedule = Boolean(promotion.startTime && promotion.endTime);
  const [saving, setSaving] = useState(false);
  const [savedLockedSchema, setSavedLockedSchema] = useState<PromotionContestActiveSchema | null>(null);
  const [formValues, setFormValues] = useState<PromotionContestFormValues>(
    createEmptyContestFormValues(),
  );

  const [requestState, {
    setToLoading,
    setToError,
    setToSuccess,
  }] = useAsyncOperationState();

  const loadContest = useCallback(() => {
    setToLoading();
    api.get<PromotionContestResponse>(`/v1/tools/promotions/${promotion.id}/contest`)
      .then((response) => {
        const { contest } = response.data;
        setFormValues(contestPayloadToFormValues(contest));
        setSavedLockedSchema(
          contest?.activeSchema === 'v1' || contest?.activeSchema === 'v2'
            ? contest.activeSchema
            : null,
        );
        setToSuccess();
      })
      .catch(() => setToError());
  }, [promotion.id, setToError, setToLoading, setToSuccess]);

  useEffect(() => {
    loadContest();
  }, [loadContest]);

  const memoizedFormValues = useMemo(() => formValues, [formValues]);

  const handleSubmit = (values: PromotionContestFormValues) => {
    if (!promotionHasSchedule) {
      showToast({
        textMessage: 'Set promotion start and end times before saving Contests configuration',
        variant: 'danger',
      });
      return;
    }

    if (!promotion.storyUuid?.trim()) {
      showToast({
        textMessage: missingStoryUuidMessage,
        variant: 'danger',
      });
      return;
    }

    setSaving(true);
    api.put(
      `/v1/tools/promotions/${promotion.id}/contest`,
      buildContestSavePayload(values),
    )
      .then((response) => {
        const { contest } = response.data;
        setFormValues(contestPayloadToFormValues(contest));
        setSavedLockedSchema(
          contest?.activeSchema === 'v1' || contest?.activeSchema === 'v2'
            ? contest.activeSchema
            : null,
        );
        showToast({ textMessage: 'Contest saved', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to save Contests configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  const handleRemove = () => {
    if (!promotionHasSchedule) {
      showToast({
        textMessage: 'Set promotion start and end times before removing Contests configuration',
        variant: 'danger',
      });
      return;
    }

    setSaving(true);
    api.put(
      `/v1/tools/promotions/${promotion.id}/contest`,
      buildContestSavePayload(createEmptyContestFormValues(), { remove: true }),
    )
      .then((response) => {
        const { contest } = response.data;
        setFormValues(contestPayloadToFormValues(contest));
        setSavedLockedSchema(null);
        showToast({ textMessage: 'Contest removed', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to remove Contests configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  if (requestState !== RequestState.Success) {
    return <LoadingState requestState={requestState} />;
  }

  return (
    <div className="mt-3">
      {!promotionHasSchedule && isEditor && (
        <Alert variant="warning">
          Set the promotion start and end times above before configuring Contests.
          Contest timing follows the promotion schedule.
        </Alert>
      )}

      <ContestForm
        formValues={memoizedFormValues}
        savedLockedSchema={savedLockedSchema}
        promotionIdentifier={promotion.identifier}
        promotionStoryUuid={promotion.storyUuid}
        onSubmit={handleSubmit}
        onRemove={handleRemove}
        onDirtyChange={onDirtyChange}
        isLoading={saving}
        disabled={!isEditor || !promotionHasSchedule}
      />
    </div>
  );
}
